/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import * as S from './genericSearchableDropdownStyled';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { IconButton, InputAdornment } from '@mui/material';
import DropdownItem from './DropdownItem';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

type GenericSearchableDropdownProps = {
    selectedType: string;
    mobile?: boolean;
    handleSelectedType: (selectedType: string) => void;
    dropdownMenuPrograms: any;
    clientProfile?: boolean;
    customStyleForSearchbar?: boolean;
    agreementPage?: boolean;
    dashboard?: boolean;
    onboarding?: boolean;
    desktopShadow?: boolean;
    purple?: boolean;
    style?: any;
    label?: string;
    handleNavigation?: boolean;
    width?: string;
    isDisabled?: boolean;
    openOnMount?: boolean;
};

const GenericSearchableDropdown = ({
    selectedType,
    mobile,
    handleSelectedType,
    dropdownMenuPrograms,
    clientProfile,
    customStyleForSearchbar,
    agreementPage,
    dashboard,
    onboarding,
    desktopShadow,
    purple,
    style,
    label,
    handleNavigation,
    width,
    isDisabled,
    openOnMount,
}: GenericSearchableDropdownProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const location = useLocation();
    const { appID } = useParams();
    const [isAutocompleteOpen, setAutocompleteOpen] = useState(openOnMount || false);
    const [inputFocused, setInputFocused] = useState(false);
    const navigate = useNavigate();

    const handleOptionClick = (event: any, selectedType: string) => {
        event.stopPropagation();
        setAutocompleteOpen(false);
        if (handleNavigation) {
            if (appID) {
                if (typeof selectedType === 'string' && selectedType.startsWith('/')) {
                    navigate(selectedType, { replace: true });
                } else {
                    const segments = location.pathname.split('/');
                    segments[segments.length - 1] = selectedType;
                    const newPath = segments.join('/');
                    navigate(newPath, { replace: true });
                }
            } else {
                navigate(`${selectedType}`);
            }
        }
        handleSelectedType(selectedType);
    };

    return (
        <S.LeftDropdown
            id="retainer-dropdown"
            onboarding={onboarding}
            dashboard={dashboard}
            customStyleForSearchbar={customStyleForSearchbar}
            clientProfile={clientProfile}
            mobile={mobile}
            agreementPage={agreementPage}
            isActive={!!selectedType}
            desktopShadow={desktopShadow}
            purple={purple}
            style={style}
            width={width}
        >
            <Autocomplete
                disablePortal={false}
                open={isAutocompleteOpen}
                onOpen={() => setAutocompleteOpen(true)}
                onClose={() => setAutocompleteOpen(false)}
                disabled={isDisabled}
                sx={{
                    width: '100%',
                    marginTop: '-15px',
                    '& .MuiAutocomplete-inputRoot[class*="MuiInput-root"]': {
                        paddingRight: 0,
                    },
                }}
                componentsProps={{
                    popper: {
                        sx: {
                            zIndex: 10000,
                        },
                        placement: 'bottom-start',
                        disablePortal: false,
                        modifiers: [
                            {
                                name: 'flip',
                                enabled: true,
                            },
                            {
                                name: 'preventOverflow',
                                enabled: true,
                                options: {
                                    altAxis: true,
                                    altBoundary: true,
                                    tether: true,
                                    rootBoundary: 'viewport',
                                    padding: 8,
                                },
                            },
                        ],
                    },
                    paper: {
                        sx: {
                            zIndex: 10000,
                            marginTop: '4px',
                        },
                    },
                }}
                value={
                    dropdownMenuPrograms.find((option: any) => option.value === selectedType) ||
                    null
                }
                onChange={(event, newValue) => {
                    if (newValue) {
                        handleOptionClick(event, newValue.value);
                    }
                }}
                options={dropdownMenuPrograms}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                    <TextField
                        onFocus={() => setInputFocused(true)}
                        onBlur={() => setInputFocused(false)}
                        {...params}
                        label={
                            label
                                ? t(`${label}`, { lng: language })
                                : t('selectType', { lng: language })
                        }
                        variant="standard"
                        sx={{
                            width: '100%',
                            '& label': {
                                opacity:
                                    selectedType ||
                                    (inputFocused && selectedType === '') ||
                                    isAutocompleteOpen
                                        ? 0
                                        : 1,
                            },
                            '& .MuiInput-underline:before': {
                                borderBottom: 'none',
                            },
                            '& .MuiInput-underline:after': {
                                borderBottom: 'none',
                            },
                            '&:hover .MuiInput-underline:before': {
                                borderBottom: 'none !important',
                            },
                        }}
                        slotProps={{
                            ...params.slotProps,
                            input: {
                                ...params.slotProps.input,
                                endAdornment: !isDisabled && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setAutocompleteOpen(!isAutocompleteOpen)}
                                            edge="end"
                                            tabIndex={-1}
                                            style={{ padding: '4px' }}
                                        >
                                            {isAutocompleteOpen ? (
                                                <ExpandLessIcon style={{ color: 'black' }} />
                                            ) : (
                                                <ExpandMoreIcon style={{ color: 'black' }} />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <div key={option.value} style={{ overflow: 'visible' }}>
                        <DropdownItem
                            onClick={(e) => handleOptionClick(e, option.value)}
                            style={{
                                margin: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                color: option.display_color,
                            }}
                        >
                            {option.label}
                        </DropdownItem>
                    </div>
                )}
                disableClearable
                ListboxProps={{ style: { maxHeight: 275 } }}
            />
        </S.LeftDropdown>
    );
};

export default GenericSearchableDropdown;
